/** Decision stored after the visitor interacts with the cookie banner. */
export type CookieConsentChoice = 'accepted' | 'essential';

export interface CookieConsentRecord {
  choice: CookieConsentChoice;
  /** ISO timestamp when the choice was saved. */
  at: string;
  /** Schema for future category expansions. */
  v: 1;
}

export function cookieConsentStorageKey(scope: string): string {
  const safe = (scope || 'app').trim().toLowerCase().replace(/[^a-z0-9._-]+/g, '-');
  return `platzona.cookieConsent.${safe}`;
}

export function readCookieConsent(scope: string): CookieConsentRecord | null {
  if (typeof localStorage === 'undefined') return null;
  try {
    const raw = localStorage.getItem(cookieConsentStorageKey(scope));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<CookieConsentRecord>;
    if (parsed.choice !== 'accepted' && parsed.choice !== 'essential') return null;
    return {
      choice: parsed.choice,
      at: typeof parsed.at === 'string' ? parsed.at : new Date().toISOString(),
      v: 1,
    };
  } catch {
    return null;
  }
}

export function writeCookieConsent(scope: string, choice: CookieConsentChoice): CookieConsentRecord {
  const record: CookieConsentRecord = {
    choice,
    at: new Date().toISOString(),
    v: 1,
  };
  if (typeof localStorage !== 'undefined') {
    try {
      localStorage.setItem(cookieConsentStorageKey(scope), JSON.stringify(record));
    } catch {
      /* private mode / quota */
    }
  }
  if (typeof window !== 'undefined') {
    window.dispatchEvent(
      new CustomEvent('platzona-cookie-consent', { detail: { scope, ...record } }),
    );
  }
  return record;
}

export function clearCookieConsent(scope: string): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.removeItem(cookieConsentStorageKey(scope));
  } catch {
    /* ignore */
  }
}

/** True when the banner should be shown (no stored decision yet). */
export function shouldShowCookieConsent(scope: string): boolean {
  return readCookieConsent(scope) == null;
}
